import React, { useState, useEffect, useRef, useCallback } from 'react'
import * as api from '../../services/api.js'

// ── Constants ─────────────────────────────────────────────────────────────────

const BLOCK_TYPES = [
  { type: 'text',         label: 'Text',         icon: '¶',  desc: 'Paragraph' },
  { type: 'heading1',     label: 'Heading 1',    icon: 'H1', desc: 'Large heading' },
  { type: 'heading2',     label: 'Heading 2',    icon: 'H2', desc: 'Medium heading' },
  { type: 'divider',      label: 'Divider',      icon: '—',  desc: 'Horizontal rule' },
  { type: 'checklist',    label: 'Checklist',    icon: '✓',  desc: 'Checkbox list' },
  { type: 'callout',      label: 'Callout',      icon: '💡', desc: 'Highlighted note' },
  { type: 'toggle',       label: 'Toggle',       icon: '▶',  desc: 'Collapsible section' },
  { type: 'custom_field', label: 'Custom field', icon: '⊟',  desc: 'Label + value' },
]

const SLASH_MAP = {
  '/heading1': 'heading1', '/h1': 'heading1',
  '/heading2': 'heading2', '/h2': 'heading2',
  '/divider':  'divider',  '/hr': 'divider',
  '/check':    'checklist','/checklist': 'checklist', '/todo': 'checklist',
  '/callout':  'callout',  '/note': 'callout',
  '/toggle':   'toggle',
  '/field':    'custom_field', '/custom': 'custom_field',
}

const COVER_COLORS = ['#f5f0e8','#fde8e8','#e8f5ed','#ede8f5','#e8f0f5','#f5f0e0','#e8f5f5','#fef9c3']
const CALLOUT_COLORS = ['#fef9c3','#fde8e8','#e8f5ed','#ede8f5','#e8f0f5','#f5f0e8']
const EMOJI_OPTIONS  = ['📄','📝','📋','📚','🌿','✨','🎨','🗺','📊','💡','🌙','⭐','🎯','💪','🏃','🧘','🍳','🌱','✅','🔖']

function defaultContent(type) {
  switch (type) {
    case 'text':         return { text: '' }
    case 'heading1':     return { text: '' }
    case 'heading2':     return { text: '' }
    case 'divider':      return {}
    case 'checklist':    return { items: [{ text: '', checked: false }] }
    case 'callout':      return { icon: '💡', text: '', color: '#fef9c3' }
    case 'toggle':       return { title: '', content: '', open: false }
    case 'custom_field': return { label: '', value: '' }
    default:             return {}
  }
}

function parseContent(raw) {
  if (typeof raw === 'object' && raw !== null) return raw
  try { return JSON.parse(raw || '{}') } catch { return {} }
}

function newLocalBlock(type, pageId, position) {
  return {
    id:         `local-${Date.now()}-${Math.random()}`,
    page_id:    pageId,
    block_type: type,
    content:    defaultContent(type),
    position,
    _local:     true,
  }
}

// ── SaveIndicator ─────────────────────────────────────────────────────────────

function SaveIndicator({ state }) {
  if (state === 'idle')   return <span className="save-indicator hidden">Saved ✓</span>
  if (state === 'saving') return <span className="save-indicator saving">Saving…</span>
  if (state === 'saved')  return <span className="save-indicator saved">Saved ✓</span>
  return null
}

// ── EmojiPicker ───────────────────────────────────────────────────────────────

function EmojiPicker({ onSelect, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose])

  return (
    <div className="emoji-picker-popup" ref={ref} style={{ position: 'absolute', zIndex: 300 }}>
      {EMOJI_OPTIONS.map(e => (
        <button key={e} className="emoji-option" onClick={() => { onSelect(e); onClose() }}>{e}</button>
      ))}
    </div>
  )
}

// ── BlockTypePicker ───────────────────────────────────────────────────────────

function BlockTypePicker({ onSelect, onClose }) {
  const ref = useRef(null)
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose])

  return (
    <div className="block-type-picker" ref={ref}>
      {BLOCK_TYPES.map(bt => (
        <button key={bt.type} className="btp-option" onClick={() => { onSelect(bt.type); onClose() }}>
          <span className="btp-icon">{bt.icon}</span>
          <span>{bt.label}</span>
        </button>
      ))}
    </div>
  )
}

// ── SlashDropdown ─────────────────────────────────────────────────────────────

function SlashDropdown({ query, onSelect, onClose }) {
  const [hi, setHi] = useState(0)
  const filtered = BLOCK_TYPES.filter(bt =>
    bt.type !== 'text' && (
      query === '' ||
      bt.label.toLowerCase().includes(query.toLowerCase()) ||
      bt.type.includes(query.toLowerCase())
    )
  )

  useEffect(() => { setHi(0) }, [query])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => (h + 1) % filtered.length) }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setHi(h => (h - 1 + filtered.length) % filtered.length) }
      if (e.key === 'Enter') { e.preventDefault(); if (filtered[hi]) onSelect(filtered[hi].type) }
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [filtered, hi, onSelect, onClose])

  if (!filtered.length) return null

  return (
    <div className="slash-dropdown">
      {filtered.map((bt, i) => (
        <button
          key={bt.type}
          className={`slash-option${i === hi ? ' highlighted' : ''}`}
          onClick={() => onSelect(bt.type)}
        >
          <span className="slash-option-icon">{bt.icon}</span>
          <div>
            <div className="slash-option-label">{bt.label}</div>
            <div className="slash-option-desc">{bt.desc}</div>
          </div>
        </button>
      ))}
    </div>
  )
}

// ── Individual block renderers ────────────────────────────────────────────────

function TextBlock({ block, onChange, onKeyDown, placeholder }) {
  const ref = useRef(null)
  const lastVal = useRef(block.content?.text || '')

  useEffect(() => {
    if (!ref.current) return
    if (ref.current.innerText !== lastVal.current) {
      ref.current.innerText = lastVal.current
    }
  }, [])

  function handleInput() {
    const text = ref.current?.innerText || ''
    lastVal.current = text
    onChange({ text })
  }

  return (
    <div
      ref={ref}
      className="block-editable"
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder || 'Start writing, or type / for commands…'}
      onInput={handleInput}
      onKeyDown={onKeyDown}
    />
  )
}

function ChecklistBlock({ block, onChange, onKeyDown }) {
  const items = block.content?.items || [{ text: '', checked: false }]

  function updateItem(i, updates) {
    const next = items.map((item, idx) => idx === i ? { ...item, ...updates } : item)
    onChange({ items: next })
  }

  function addItem(afterIdx) {
    const next = [...items]
    next.splice(afterIdx + 1, 0, { text: '', checked: false })
    onChange({ items: next })
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-checklist="${block.id}"] .checklist-item-input`)
      if (inputs[afterIdx + 1]) inputs[afterIdx + 1].focus()
    }, 30)
  }

  function removeItem(i) {
    if (items.length <= 1) return
    const next = items.filter((_, idx) => idx !== i)
    onChange({ items: next })
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-checklist="${block.id}"] .checklist-item-input`)
      const focusIdx = Math.max(0, i - 1)
      if (inputs[focusIdx]) inputs[focusIdx].focus()
    }, 30)
  }

  return (
    <div className="checklist-items" data-checklist={block.id}>
      {items.map((item, i) => (
        <div key={i} className="checklist-item">
          <input
            type="checkbox"
            checked={item.checked}
            onChange={e => updateItem(i, { checked: e.target.checked })}
          />
          <input
            className={`checklist-item-input${item.checked ? ' done' : ''}`}
            value={item.text}
            placeholder="List item…"
            onChange={e => updateItem(i, { text: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter')     { e.preventDefault(); addItem(i) }
              if (e.key === 'Backspace' && !item.text) { e.preventDefault(); removeItem(i) }
              if (e.key === 'Tab')       { e.preventDefault(); updateItem(i, { text: '  ' + item.text }) }
              onKeyDown?.(e)
            }}
          />
        </div>
      ))}
      <button className="checklist-add-item" onClick={() => addItem(items.length - 1)}>
        + Add item
      </button>
    </div>
  )
}

function CalloutBlock({ block, onChange }) {
  const content = block.content || {}
  const [showEmoji, setShowEmoji] = useState(false)

  return (
    <div className="callout-block" style={{ background: content.color || '#fef9c3' }}>
      <div style={{ position: 'relative' }}>
        <button className="callout-icon-btn" onClick={() => setShowEmoji(v => !v)}>
          {content.icon || '💡'}
        </button>
        {showEmoji && (
          <EmojiPicker
            onSelect={icon => onChange({ ...content, icon })}
            onClose={() => setShowEmoji(false)}
          />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <textarea
          className="callout-text"
          value={content.text || ''}
          placeholder="Write a note…"
          rows={2}
          onChange={e => onChange({ ...content, text: e.target.value })}
        />
        <div className="callout-colors">
          {CALLOUT_COLORS.map(c => (
            <div
              key={c}
              className={`callout-color-dot${content.color === c ? ' selected' : ''}`}
              style={{ background: c }}
              onClick={() => onChange({ ...content, color: c })}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function ToggleBlock({ block, onChange }) {
  const content = block.content || {}
  const open    = content.open ?? false

  return (
    <div className="toggle-block">
      <div className="toggle-title-row" onClick={() => onChange({ ...content, open: !open })}>
        <span className={`toggle-chevron${open ? ' open' : ''}`}>▶</span>
        <input
          className="toggle-title-input"
          value={content.title || ''}
          placeholder="Toggle title…"
          onChange={e => onChange({ ...content, title: e.target.value })}
          onClick={e => e.stopPropagation()}
        />
      </div>
      {open && (
        <div className="toggle-content">
          <textarea
            className="toggle-content-input"
            value={content.content || ''}
            placeholder="Expand to write…"
            rows={3}
            onChange={e => onChange({ ...content, content: e.target.value })}
          />
        </div>
      )}
    </div>
  )
}

function CustomFieldBlock({ block, onChange }) {
  const content = block.content || {}
  return (
    <div className="custom-field-block">
      <input
        className="cf-label-input"
        value={content.label || ''}
        placeholder="Label"
        onChange={e => onChange({ ...content, label: e.target.value })}
      />
      <input
        className="cf-value-input"
        value={content.value || ''}
        placeholder="Value"
        onChange={e => onChange({ ...content, value: e.target.value })}
      />
    </div>
  )
}

// ── BlockItem ─────────────────────────────────────────────────────────────────

function BlockItem({
  block, index, totalBlocks,
  onContentChange, onDelete, onAddBelow, onTypeChange,
  onDragStart, dropIndicator,
}) {
  const [showTypePicker, setShowTypePicker] = useState(false)
  const [showSlash, setShowSlash]           = useState(false)
  const [slashQuery, setSlashQuery]         = useState('')
  const [focused, setFocused]               = useState(false)
  const slashRef = useRef(false)
  const content  = parseContent(block.content)

  function handleTextChange(updates) {
    const text = updates.text || ''
    // Slash detection: show dropdown when user types '/' in empty block
    if (!slashRef.current && text === '/') {
      slashRef.current = true
      setShowSlash(true)
      setSlashQuery('')
      return
    }
    if (slashRef.current) {
      if (!text.startsWith('/')) {
        slashRef.current = false
        setShowSlash(false)
      } else {
        setSlashQuery(text.slice(1))
      }
      return
    }
    onContentChange(block.id, updates)
  }

  function handleSlashSelect(type) {
    slashRef.current = false
    setShowSlash(false)
    setSlashQuery('')
    onTypeChange(block.id, type)
  }

  function handleTextKeyDown(e) {
    if (showSlash) return
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onAddBelow(index, 'text')
    }
    if (e.key === 'Backspace') {
      const text = e.target?.innerText || ''
      if (!text.trim() && totalBlocks > 1) {
        e.preventDefault()
        onDelete(block.id, index)
      }
    }
    if (e.key === 'Escape') e.target?.blur()
  }

  const isText    = block.block_type === 'text'
  const isHeading = block.block_type === 'heading1' || block.block_type === 'heading2'

  return (
    <>
      {dropIndicator === 'before' && (
        <div className="block-drop-indicator active" />
      )}
      <div
        className={`block block--${block.block_type}${focused ? ' block--focused' : ''}`}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <div
          className="block-handle"
          onMouseDown={e => onDragStart(e, index)}
          title="Drag to reorder"
        >
          ⠿
        </div>

        <div className="block-content" style={{ position: 'relative' }}>
          {(isText || isHeading) && (
            <>
              <TextBlock
                block={block}
                onChange={handleTextChange}
                onKeyDown={handleTextKeyDown}
                placeholder={isText ? 'Start writing, or type / for commands…' : 'Heading…'}
              />
              {showSlash && (
                <SlashDropdown
                  query={slashQuery}
                  onSelect={handleSlashSelect}
                  onClose={() => { setShowSlash(false); slashRef.current = false }}
                />
              )}
            </>
          )}

          {block.block_type === 'divider' && (
            <hr />
          )}

          {block.block_type === 'checklist' && (
            <ChecklistBlock
              block={block}
              onChange={items => onContentChange(block.id, items)}
              onKeyDown={() => {}}
            />
          )}

          {block.block_type === 'callout' && (
            <CalloutBlock
              block={block}
              onChange={c => onContentChange(block.id, c)}
            />
          )}

          {block.block_type === 'toggle' && (
            <ToggleBlock
              block={block}
              onChange={c => onContentChange(block.id, c)}
            />
          )}

          {block.block_type === 'custom_field' && (
            <CustomFieldBlock
              block={block}
              onChange={c => onContentChange(block.id, c)}
            />
          )}
        </div>

        <div className="block-actions">
          <button
            className="block-action-btn"
            title="Add block below"
            onClick={() => onAddBelow(index, 'text')}
          >+</button>
          <button
            className="block-action-btn"
            title="Change block type"
            onClick={() => setShowTypePicker(v => !v)}
          >⋯</button>
          <button
            className="block-action-btn"
            title="Delete block"
            onClick={() => {
              if (totalBlocks <= 1) {
                alert('Every page needs at least one block.')
                return
              }
              onDelete(block.id, index)
            }}
          >✕</button>
        </div>

        {showTypePicker && (
          <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 200 }}>
            <BlockTypePicker
              onSelect={type => { onTypeChange(block.id, type); setShowTypePicker(false) }}
              onClose={() => setShowTypePicker(false)}
            />
          </div>
        )}
      </div>

      {dropIndicator === 'after' && (
        <div className="block-drop-indicator active" />
      )}
    </>
  )
}

// ── PageEditor ────────────────────────────────────────────────────────────────

export default function PageEditor({ page, onBack, onPageUpdated }) {
  const [blocks, setBlocks]       = useState([])
  const [title, setTitle]         = useState(page.title || 'Untitled')
  const [icon, setIcon]           = useState(page.icon || '📄')
  const [coverColor, setCover]    = useState(page.cover_color || '#f5f0e8')
  const [coverImage, setCoverImage] = useState(page.cover_image || null)
  const [saveState, setSaveState] = useState('idle')
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [templateName, setTemplateName]         = useState('')
  const [templateDesc, setTemplateDesc]         = useState('')
  const [dragIdx, setDragIdx]     = useState(-1)
  const [dropIdx, setDropIdx]     = useState(-1)
  const saveTimer  = useRef(null)
  const blocksRef  = useRef(blocks)
  const fileInputRef = useRef(null)

  useEffect(() => { blocksRef.current = blocks }, [blocks])

  useEffect(() => {
    api.getPageBlocks(page.id).then(raw => {
      const parsed = (raw || []).map(b => ({ ...b, content: parseContent(b.content) }))
      if (!parsed.length) {
        // Empty page: create one default text block
        const local = newLocalBlock('text', page.id, 0)
        setBlocks([local])
        api.createPageBlock({ page_id: page.id, block_type: 'text', content: { text: '' }, position: 0 })
          .then(res => {
            if (res?.id) setBlocks(prev => prev.map(b => b.id === local.id ? { ...b, id: res.id, _local: false } : b))
          })
      } else {
        setBlocks(parsed)
      }
    })
  }, [page.id])

  function triggerSave(fn) {
    setSaveState('saving')
    clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      await fn()
      setSaveState('saved')
      setTimeout(() => setSaveState('idle'), 2000)
    }, 1500)
  }

  function handleContentChange(blockId, updates) {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, content: { ...parseContent(b.content), ...updates } } : b))
    const blk = blocksRef.current.find(b => b.id === blockId)
    if (!blk?._local) {
      triggerSave(() => api.updatePageBlock(blockId, page.id, { content: { ...parseContent(blk?.content), ...updates } }))
    }
  }

  function handleTypeChange(blockId, newType) {
    const blk = blocks.find(b => b.id === blockId)
    const newContent = defaultContent(newType)
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, block_type: newType, content: newContent } : b))
    if (!blk?._local) {
      triggerSave(() => api.updatePageBlock(blockId, page.id, { block_type: newType, content: newContent }))
    }
  }

  async function handleAddBelow(index, type) {
    const position = index + 1
    const local    = newLocalBlock(type, page.id, position)
    const newBlocks = [...blocks]
    newBlocks.splice(position, 0, local)
    // Update positions
    const reindexed = newBlocks.map((b, i) => ({ ...b, position: i }))
    setBlocks(reindexed)

    try {
      const res = await api.createPageBlock({ page_id: page.id, block_type: type, content: defaultContent(type), position })
      if (res?.id) {
        setBlocks(prev => prev.map(b => b.id === local.id ? { ...b, id: res.id, _local: false } : b))
      }
    } catch (err) {
      console.warn('addBelow failed', err)
    }
  }

  async function handleDelete(blockId, index) {
    if (blocks.length <= 1) { alert('Every page needs at least one block.'); return }
    setBlocks(prev => prev.filter(b => b.id !== blockId))
    const blk = blocks.find(b => b.id === blockId)
    if (!blk?._local) await api.deletePageBlock(blockId, page.id)
  }

  function handleTitleBlur() {
    const t = title.trim() || 'Untitled'
    setTitle(t)
    api.updateMyPage(page.id, { title: t })
    onPageUpdated?.({ ...page, title: t })
  }

  function handleCoverColor(color) {
    setCover(color)
    api.updateMyPage(page.id, { cover_color: color })
    onPageUpdated?.({ ...page, cover_color: color })
  }

  function handleIconSelect(e) {
    setIcon(e)
    setShowIconPicker(false)
    api.updateMyPage(page.id, { icon: e })
    onPageUpdated?.({ ...page, icon: e })
  }

  function handleCoverImageUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const img = new Image()
      img.onload = () => {
        const MAX = 1200
        let { width, height } = img
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX }
          else { width = Math.round(width * MAX / height); height = MAX }
        }
        const canvas = document.createElement('canvas')
        canvas.width  = width
        canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        const base64 = canvas.toDataURL('image/jpeg', 0.85)
        setCoverImage(base64)
        api.updateMyPage(page.id, { cover_image: base64 })
        onPageUpdated?.({ ...page, cover_image: base64 })
      }
      img.src = ev.target.result
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  function handleRemoveCoverImage() {
    setCoverImage(null)
    api.updateMyPage(page.id, { cover_image: null })
    onPageUpdated?.({ ...page, cover_image: null })
  }

  // ── Drag reorder ────────────────────────────────────────────────────────────

  function handleDragStart(e, idx) {
    e.preventDefault()
    setDragIdx(idx)
    setDropIdx(idx)

    function onMove(e) {
      const blockEls = document.querySelectorAll('.blocks-list .block')
      let closest = idx
      let closestDist = Infinity
      blockEls.forEach((el, i) => {
        const rect = el.getBoundingClientRect()
        const mid  = rect.top + rect.height / 2
        const dist = Math.abs(e.clientY - mid)
        if (dist < closestDist) { closestDist = dist; closest = i }
      })
      setDropIdx(closest)
    }

    function onUp() {
      setDragIdx(prev => {
        setDropIdx(d => {
          if (prev !== -1 && d !== -1 && prev !== d) {
            setBlocks(blks => {
              const next = [...blks]
              const [item] = next.splice(prev, 1)
              next.splice(d, 0, item)
              const reindexed = next.map((b, i) => ({ ...b, position: i }))
              api.reorderPageBlocks(reindexed.map(b => ({ id: b.id, position: b.position })))
              return reindexed
            })
          }
          return -1
        })
        return -1
      })
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // ── Save as template ────────────────────────────────────────────────────────

  async function handleSaveTemplate() {
    if (!templateName.trim()) return
    const data = {
      blocks: blocks.map(b => ({ block_type: b.block_type, content: b.content }))
    }
    await api.saveMySpaceTemplate({
      title:         templateName.trim(),
      description:   templateDesc.trim() || null,
      template_type: 'page',
      data,
    })
    setShowSaveTemplate(false)
    setTemplateName('')
    setTemplateDesc('')
  }

  return (
    <div className="page-editor">
      {/* Back */}
      <button className="page-editor-back" onClick={onBack}>
        ← My Pages
      </button>

      {/* Cover */}
      <div
        className="page-editor-cover"
        style={coverImage
          ? { backgroundImage: `url(${coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: coverColor }
        }
      />

      {/* Cover color picker + image upload */}
      <div className="cover-color-row">
        <span style={{ fontSize: '0.68rem', color: 'var(--stone)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Cover</span>
        {!coverImage && COVER_COLORS.map(c => (
          <div
            key={c}
            className={`cover-swatch${coverColor === c ? ' selected' : ''}`}
            style={{ background: c }}
            onClick={() => handleCoverColor(c)}
          />
        ))}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleCoverImageUpload}
        />
        {coverImage ? (
          <button className="cover-remove-btn" onClick={handleRemoveCoverImage}>
            ✕ Remove image
          </button>
        ) : (
          <button className="cover-upload-btn" onClick={() => fileInputRef.current?.click()}>
            📷 Upload image
          </button>
        )}
      </div>

      {/* Icon + title */}
      <div className="page-editor-header">
        <div style={{ position: 'relative' }}>
          <button className="page-icon-btn" onClick={() => setShowIconPicker(v => !v)}>
            {icon}
          </button>
          {showIconPicker && (
            <EmojiPicker onSelect={handleIconSelect} onClose={() => setShowIconPicker(false)} />
          )}
        </div>
        <input
          className="page-title-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onBlur={handleTitleBlur}
          placeholder="Untitled"
        />
      </div>

      {/* Meta row */}
      <div className="page-editor-meta">
        <SaveIndicator state={saveState} />
        <button
          className="ghost-btn"
          style={{ fontSize: '0.72rem', marginLeft: 'auto' }}
          onClick={() => { setShowSaveTemplate(v => !v); setTemplateName(title) }}
        >
          Save as template
        </button>
      </div>

      {/* Save as template form */}
      {showSaveTemplate && (
        <div className="ms-card save-template-form" style={{ marginBottom: '1rem' }}>
          <input
            className="field-input"
            placeholder="Template name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <input
            className="field-input"
            placeholder="Description (optional)"
            value={templateDesc}
            onChange={e => setTemplateDesc(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="save-btn ready" style={{ fontSize: '0.82rem' }} onClick={handleSaveTemplate}>
              Save template
            </button>
            <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={() => setShowSaveTemplate(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Blocks */}
      <div className="blocks-list">
        {blocks.map((block, index) => (
          <BlockItem
            key={block.id}
            block={block}
            index={index}
            totalBlocks={blocks.length}
            onContentChange={handleContentChange}
            onDelete={handleDelete}
            onAddBelow={handleAddBelow}
            onTypeChange={handleTypeChange}
            onDragStart={handleDragStart}
            dropIndicator={
              dragIdx !== -1 && dropIdx !== -1
                ? (index === dropIdx && dragIdx < dropIdx ? 'after'
                  : index === dropIdx && dragIdx > dropIdx ? 'before'
                  : null)
                : null
            }
          />
        ))}
      </div>

      {/* Add block at bottom */}
      <button
        className="ghost-btn"
        style={{ fontSize: '0.8rem', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}
        onClick={() => handleAddBelow(blocks.length - 1, 'text')}
      >
        + Add block
      </button>
    </div>
  )
}
